import { useCallback, useState } from "react";
import { useAuth } from "../contexts/AuthContext.jsx";
import { useToast } from "../components/ToastContainer.jsx";
import { useConfirm } from "../contexts/ConfirmDialogContext.jsx";
import { useJobStatus } from "../contexts/JobStatusContext.jsx";

// Delete one sheet of a load, or reprocess the whole load: both start a job, shown in the jobs drawer.
// Each action answers the job id, or null when it was cancelled or refused.
export function useLoadActions() {
  const { fetchWithAuth } = useAuth();
  const { addToast } = useToast();
  const confirm = useConfirm();
  const { addJob, openDrawer } = useJobStatus();
  const [deletingUuid, setDeletingUuid] = useState(null);
  const [reprocessingUuid, setReprocessingUuid] = useState(null);

  const startJob = useCallback(
    async (url, options, label, item) => {
      const response = await fetchWithAuth(url, options);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }
      const { job_id } = await response.json();
      addJob(job_id, {
        excel_name: `${label} ${item.shop_sign} - ${item.client}`,
        shop_sign: item.shop_sign,
        client: item.client,
        year: item.year,
        week: item.week,
        category: item.category,
      });
      openDrawer();
      return job_id;
    },
    [fetchWithAuth, addJob, openDrawer]
  );

  const deleteSheet = useCallback(
    async (item, message = "Êtes-vous sûr de vouloir supprimer cette ingestion ?") => {
      if (!item?.uuid) return null;
      const isConfirmed = await confirm({
        title: "Supprimer",
        message,
        confirmText: "OK",
        cancelText: "Annuler",
        type: "error",
      });
      if (!isConfirmed) return null;

      setDeletingUuid(item.uuid);
      try {
        return await startJob(`/api/history/${item.uuid}`, { method: "DELETE" }, "Suppression", item);
      } catch (error) {
        addToast({ title: "Erreur de suppression", message: error.message, type: "error" });
        return null;
      } finally {
        setDeletingUuid(null);
      }
    },
    [confirm, startJob, addToast]
  );

  // sheetsOverride: the edited mapping from the reprocess dialog, or null to reuse the saved one
  const reprocessLoad = useCallback(
    async (item, sheetsOverride) => {
      setReprocessingUuid(item.uuid);
      try {
        const options = sheetsOverride
          ? { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ sheets: sheetsOverride }) }
          : { method: "POST" };
        const jobId = await startJob(`/api/history/${item.uuid}/reprocess`, options, "Retraitement", item);
        addToast({ title: "Retraitement lancé", message: "Le job est en cours de traitement.", type: "info" });
        return jobId;
      } catch (error) {
        addToast({ title: "Erreur de retraitement", message: error.message, type: "error" });
        return null;
      } finally {
        setReprocessingUuid(null);
      }
    },
    [startJob, addToast]
  );

  return { deleteSheet, reprocessLoad, deletingUuid, reprocessingUuid };
}
